import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Sun, Calendar, RefreshCw} from 'lucide-react';
import { calculateBirthChartWithLibrary } from '../../astro/horoscopeCalculator';
import useCityToCoordinates from './useCityToCoordinates';
import ChartDrawer from './ChartDrawer';

const majorCities = [
  { name: "New York, NY, USA", lat: 40.7128, lon: -74.0060 },
  { name: "Los Angeles, CA, USA", lat: 34.0522, lon: -118.2437 },
  { name: "Chicago, IL, USA", lat: 41.8781, lon: -87.6298 },
  { name: "London, UK", lat: 51.5074, lon: -0.1278 },
  { name: "Paris, France", lat: 48.8566, lon: 2.3522 },
  { name: "Berlin, Germany", lat: 52.52, lon: 13.405 },
  { name: "Amsterdam, Netherlands", lat: 52.3676, lon: 4.9041 },
  { name: "Tokyo, Japan", lat: 35.6762, lon: 139.6503 },
  { name: "Sydney, Australia", lat: -33.8688, lon: 151.2093 },
  { name: "Toronto, Canada", lat: 43.6532, lon: -79.3832 },
  { name: "Mexico City, Mexico", lat: 19.4326, lon: -99.1332 },
  { name: "Buenos Aires, Argentina", lat: -34.6118, lon: -58.396 },
  { name: "Cape Town, South Africa", lat: -33.9249, lon: 18.4241 },
  { name: "Moscow, Russia", lat: 55.7558, lon: 37.6176 },
  { name: "Beijing, China", lat: 39.9042, lon: 116.4074 },
  { name: "Delhi, India", lat: 28.7041, lon: 77.1025 },
  { name: "Bangkok, Thailand", lat: 13.7563, lon: 100.5018 },
  { name: "Rio de Janeiro, Brazil", lat: -22.9068, lon: -43.1729 }
];

const SolarReturnExperience = () => {
  const [page, setPage] = useState(0); // 0 = form, 1 = result
  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: '',
    birthPlace: '',
    latitude: '',
    longitude: '',
    solarReturnYear: new Date().getFullYear().toString()
  });
  const [isCustomPlace, setIsCustomPlace] = useState(false);
  const [customPlace, setCustomPlace] = useState('');
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [solarReturnData, setSolarReturnData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { fetchCoordinates, loading: geocodingLoading, error: geocodingError } = useCityToCoordinates();

  const handleChange = ({ target: { name, value } }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceChange = async (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'custom') {
      setIsCustomPlace(true);
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '' }));
    } else if (selectedValue === '') {
      setIsCustomPlace(false);
      setShowCoordinates(false);
      setFormData(prev => ({ ...prev, birthPlace: '', latitude: '', longitude: '' }));
    } else {
      setIsCustomPlace(false);
      const selectedCity = majorCities.find(city => city.name === selectedValue);
      if (selectedCity) {
        setFormData(prev => ({
          ...prev,
          birthPlace: selectedCity.name,
          latitude: selectedCity.lat.toString(),
          longitude: selectedCity.lon.toString()
        }));
        setShowCoordinates(true);
      }
    }
  };

  const handleCustomPlaceSubmit = async () => {
    if (!customPlace.trim()) return;
    try {
      const coords = await fetchCoordinates(customPlace);
      if (coords) {
        setFormData(prev => ({
          ...prev,
          birthPlace: customPlace,
          latitude: coords.lat.toString(),
          longitude: coords.lon.toString()
        }));
        setShowCoordinates(true);
        setIsCustomPlace(false);
        setCustomPlace('');
      }
    } catch (error) {}
  };

  const getZodiacSign = (longitude) => {
    const signs = [
      'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
      'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
    ];
    const signIndex = Math.floor(longitude / 30);
    return signs[signIndex];
  };

  const getSignEmoji = (sign) => {
    const signEmojis = {
      'Aries': '♈', 'Taurus': '♉', 'Gemini': '♊', 'Cancer': '♋',
      'Leo': '♌', 'Virgo': '♍', 'Libra': '♎', 'Scorpio': '♏',
      'Sagittarius': '♐', 'Capricorn': '♑', 'Aquarius': '♒', 'Pisces': '♓'
    };
    return signEmojis[sign] || '⭐';
  };

  const formatLongitude = (longitude) => {
    const degrees = Math.floor(longitude);
    const minutes = Math.floor((longitude - degrees) * 60);
    const sign = getZodiacSign(longitude);
    return `${degrees}° ${minutes}' ${sign}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const latitude = parseFloat(formData.latitude) || 0;
      const longitude = parseFloat(formData.longitude) || 0;
      const { birthDate, birthTime, birthPlace, solarReturnYear } = formData;
      
      if (!birthDate || !birthTime || !birthPlace) {
        throw new Error('Please enter all required fields.');
      }

      // Calculate solar return date (birthday in the target year)
      const birthDateObj = new Date(`${birthDate}T${birthTime}`);
      const solarReturnYearNum = parseInt(solarReturnYear);
      const solarReturnDate = new Date(solarReturnYearNum, birthDateObj.getMonth(), birthDateObj.getDate());
      
      // Set the time to the same time as birth
      solarReturnDate.setHours(birthDateObj.getHours());
      solarReturnDate.setMinutes(birthDateObj.getMinutes());
      solarReturnDate.setSeconds(birthDateObj.getSeconds());


      // Use the same birth chart calculation logic but with solar return date
      const chartData = await calculateBirthChartWithLibrary(solarReturnDate, latitude, longitude, true);
      
      // Add solar return specific information
      const solarReturnChart = {
        ...chartData,
        solarReturnDate: solarReturnDate.toISOString(),
        solarReturnYear: solarReturnYearNum,
        birthDate: formData.birthDate,
        birthTime: formData.birthTime,
        birthPlace: formData.birthPlace
      };

      setSolarReturnData(solarReturnChart);
      setPage(1);
    } catch (error) {
      console.error('Error calculating solar return chart:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{
        backgroundImage: `url('/img/saturnwallpaper.jpg')`,
      }}
    >
      {page === 0 && (
        <div className="ascendant-display-container">
          <div className="ascendant-header">
            <div className="ascendant-title">
              <Sun size={24} />
              <h1>Solar Return Chart</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ascendant-form">
            <div className="form-section" style={{
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="section-title" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                color: '#FFD700',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}>
                <Calendar size={20} />
                <span>Birth Information</span>
              </div>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Birth Date</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Birth Time</label>
                  <input
                    type="time"
                    name="birthTime"
                    value={formData.birthTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Birth Place</label>
                <select value={isCustomPlace ? 'custom' : formData.birthPlace} onChange={handlePlaceChange} required>
                  <option value="">Select a city...</option>
                  {majorCities.map(city => (
                    <option key={city.name} value={city.name}>{city.name}</option>
                  ))}
                  <option value="custom">Custom...</option>
                </select>
                {isCustomPlace && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input type="text" value={customPlace} onChange={e => setCustomPlace(e.target.value)} placeholder="Enter custom place" />
                    <button type="button" onClick={handleCustomPlaceSubmit} disabled={geocodingLoading} style={{ marginLeft: 8 }}>
                      {geocodingLoading ? 'Locating...' : 'Set Location'}
                    </button>
                    {geocodingError && <div style={{ color: 'red', marginTop: 4 }}>{geocodingError}</div>}
                  </div>
                )}
              </div>

              <div className="form-field">
                <label>Solar Return Year</label>
                <input
                  type="number"
                  name="solarReturnYear"
                  value={formData.solarReturnYear}
                  onChange={handleChange}
                  min="1900"
                  max="2100"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="calculate-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Sun size={18} />
                    Calculate Solar Return
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {page === 1 && solarReturnData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="has-text-centered"
        >
          <div className="card modern-card" style={{ maxWidth: 600, margin: "2rem auto", padding: "2rem", boxShadow: "0 4px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ marginBottom: "1rem" }}>
              <h2 style={{ color: "#FFD700", marginBottom: "0.5rem" }}>Solar Return Chart {solarReturnData.solarReturnYear}</h2>
              <p style={{ color: "#fff", opacity: 0.8 }}>
                {new Date(solarReturnData.solarReturnDate).toLocaleDateString()} • {solarReturnData.birthPlace}
              </p>
            </div>
            <ChartDrawer {...solarReturnData} ascendantLongitude={solarReturnData?.ascendantLongitude || 0} />
          </div>
          <motion.button
            className="button is-warning"
            style={{
              position: "fixed",
              top: 100,
              left: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={() => window.location.href = "/solarreturn"}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <ArrowLeft size={20} />
            Back
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default SolarReturnExperience; 
